import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { columnArticles } from '@/mocks/columnArticles';

export default function ColumnEditor() {
  const [activeArticle, setActiveArticle] = useState(columnArticles[0]);
  const [expanded, setExpanded] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!subscribeEmail) return;
    setSubscribing(true);
    try {
      const body = new URLSearchParams();
      body.append('email', subscribeEmail);
      body.append('column', '清鉴元衡站长专栏');
      await fetch('https://readdy.ai/api/form/d77v0rhrcamcd36daqj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubscribed(true);
    } catch {
      setSubscribed(true);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section id="column" className="relative py-24 md:py-32 overflow-hidden" style={{ background: '#080808' }}>
      {/* Doodle-style wavy border top */}
      <div className="absolute top-0 left-0 right-0 h-1 overflow-hidden">
        <svg viewBox="0 0 1440 4" preserveAspectRatio="none" className="w-full h-full">
          <path d="M0,2 Q180,0 360,2 Q540,4 720,2 Q900,0 1080,2 Q1260,4 1440,2" stroke="rgba(255,255,255,0.1)" strokeWidth="1" fill="none"/>
        </svg>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`mb-16 transition-all duration-700 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            {/* Editor Profile */}
            <div className="flex items-center gap-6">
              <div className="relative flex-shrink-0">
                <div className="w-20 h-20 rounded-full overflow-hidden border-2 border-white/20">
                  <img
                    src="https://public.readdy.ai/ai/img_res/75be98fe6f340cc7d20c75740d6d2d67.jpg"
                    alt="清鉴元衡"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-1 -right-1 w-6 h-6 flex items-center justify-center rounded-full bg-white text-black">
                  <i className="ri-quill-pen-line text-xs" />
                </div>
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>清鉴元衡</h2>
                  <span className="text-white/30 text-sm">·</span>
                  <span className="text-white/40 text-sm italic">Mr. MetAdvancing</span>
                </div>
                <p className="text-white/50 text-sm mb-3">META66创始人 · 元宇宙战略研究者 · 数字文明观察者</p>
                <div className="flex items-center gap-4 flex-wrap">
                  <a href="tel:+8613810001256" className="flex items-center gap-1.5 text-white/40 text-xs hover:text-white/70 transition-colors whitespace-nowrap">
                    <i className="ri-phone-line text-white/60" />
                    <span>(86) 138-1000-1256</span>
                  </a>
                  <span className="text-white/20">|</span>
                  <a href="tel:+8613810001256" className="flex items-center gap-1.5 text-white/40 text-xs hover:text-white/70 transition-colors whitespace-nowrap">
                    <i className="ri-wechat-line text-white/60" />
                    <span>13810001256</span>
                  </a>
                  <span className="text-white/20">|</span>
                  <a href="mailto:13810001256@126.com" className="flex items-center gap-1.5 text-white/40 text-xs hover:text-white/70 transition-colors whitespace-nowrap">
                    <i className="ri-mail-line text-white/60" />
                    <span>13810001256@126.com</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Subscribe */}
            <div className="flex-shrink-0">
              {subscribed ? (
                <div className="flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white/60 text-sm">
                  <i className="ri-check-line text-white/80" />
                  <span>已订阅专栏</span>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubscribe} className="flex items-center gap-2">
                  <input
                    type="email"
                    name="email"
                    value={subscribeEmail}
                    onChange={(e) => setSubscribeEmail(e.target.value)}
                    placeholder="订阅站长专栏"
                    required
                    className="px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-white text-sm placeholder-white/25 focus:outline-none focus:border-white/40 transition-colors w-48"
                  />
                  <button
                    type="submit"
                    disabled={subscribing}
                    className="px-5 py-2.5 rounded-full bg-white text-black text-sm font-bold hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    {subscribing ? <i className="ri-loader-4-line animate-spin" /> : '订阅'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Content Layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Article List */}
          <div className="lg:w-80 flex-shrink-0 flex flex-col gap-3">
            <p className="text-white/30 text-xs font-bold tracking-widest uppercase mb-2">最新文章</p>
            {columnArticles.map((article) => (
              <button
                key={article.id}
                onClick={() => { setActiveArticle(article); setExpanded(false); }}
                className={`text-left p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                  activeArticle.id === article.id
                    ? 'border-white/30 bg-white/5'
                    : 'border-white/8 hover:border-white/20'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs px-2 py-0.5 rounded-full border border-white/15 text-white/40">{article.category}</span>
                  <span className="text-white/20 text-xs">{article.date}</span>
                </div>
                <p className={`text-sm font-semibold leading-snug line-clamp-2 ${activeArticle.id === article.id ? 'text-white' : 'text-white/60'}`}>
                  {article.title}
                </p>
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-white/25 text-xs"><i className="ri-eye-line mr-1" />{article.views}</span>
                  <span className="text-white/25 text-xs"><i className="ri-heart-line mr-1" />{article.likes}</span>
                  <span className="text-white/25 text-xs"><i className="ri-time-line mr-1" />{article.readTime}</span>
                </div>
              </button>
            ))}
          </div>

          {/* Article Detail */}
          <div className="flex-1 min-w-0">
            <div className="p-8 rounded-2xl border border-white/10 bg-white/2">
              {/* Article Header */}
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="text-xs px-3 py-1 rounded-full border border-white/20 text-white/50">{activeArticle.category}</span>
                  <span className="text-white/30 text-xs">{activeArticle.date}</span>
                  <span className="text-white/30 text-xs"><i className="ri-time-line mr-1" />{activeArticle.readTime}</span>
                  <span className="text-white/30 text-xs"><i className="ri-eye-line mr-1" />{activeArticle.views}阅读</span>
                </div>
                <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">{activeArticle.title}</h3>
                <p className="text-white/40 text-sm italic mb-4">{activeArticle.subtitle}</p>
                <p className="text-white/60 text-sm leading-relaxed border-l-2 border-white/20 pl-4">{activeArticle.summary}</p>
              </div>

              {/* Article Content */}
              <div className={`overflow-hidden transition-all duration-500 ${expanded ? 'max-h-[2000px]' : 'max-h-48'}`}>
                <div className="text-white/50 text-sm leading-relaxed whitespace-pre-line">
                  {activeArticle.content}
                </div>
              </div>

              {!expanded && (
                <div className="relative -mt-12 pt-12 bg-gradient-to-t from-[#080808] to-transparent" />
              )}

              <button
                onClick={() => setExpanded(!expanded)}
                className="mt-4 flex items-center gap-2 text-white/50 text-sm hover:text-white/80 transition-colors cursor-pointer"
              >
                <span>{expanded ? '收起' : '阅读全文'}</span>
                {expanded ? <i className="ri-arrow-up-line" /> : <i className="ri-arrow-down-line" />}
              </button>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/8">
                {activeArticle.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full border border-white/10 text-white/30">#{tag}</span>
                ))}
                <div className="ml-auto flex items-center gap-3">
                  <button className="flex items-center gap-1.5 text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">
                    <i className="ri-heart-line" />
                    <span>{activeArticle.likes}</span>
                  </button>
                  <button className="flex items-center gap-1.5 text-white/30 text-xs hover:text-white/60 transition-colors cursor-pointer">
                    <i className="ri-share-line" />
                    <span>分享</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
